import { createAsyncThunk } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { api } from "../../../api/api";
import { APIError } from "../../../constants/types";
import { IBusinessInterruptionFormValues, IBusinessInterruptionListFetchParams, IBusinessInterruptionRecord } from "./businessPolicyBusinessInterruption.types";
import { showMessage } from "../../messages/messagesActions";
import { saveLoaderCompleted, saveLoaderProgress } from "../../messages/messagesActions";
import { IStoreState } from "../../initialStoreState";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

/**
 * Normalizes an optional date field for the API. The backend rejects empty strings for
 * date columns (e.g. "Incorrect date value: 'Invalid date' for column 'date_prepared'"),
 * so empty/whitespace/invalid values are sent as `null` instead of `""`.
 */
const normalizeDateField = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return (value as string | null) ?? null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Fetches the Business Interruption worksheet data for a given customer policy.
 * GET /business-policy/business-interruption?customer_policy_id={customerPolicyId}
 */
export const fetchBusinessInterruptionAsync = createAsyncThunk<
  IBusinessInterruptionFormValues | null,
  { customerPolicyId: string | number; incrementalNumber?: string | null },
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/fetchBusinessInterruption",
  async ({ customerPolicyId, incrementalNumber }, { rejectWithValue, dispatch }) => {
    try {
      let url = `/policy/get-policy-business-interruption?customer_policy_id=${customerPolicyId}`;
      if (incrementalNumber) {
        url += `&incremental_number=${incrementalNumber}`;
      }
      const res = await api.get(url);
      const responseData = res.data.data;
      // API returns an array of records — use the latest one (last item) for the form
      if (Array.isArray(responseData) && responseData.length > 0) {
        return responseData[responseData.length - 1];
      }
      // Handle case where response is a single object (not array)
      if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
        // If it has numeric keys like "0", "1", it's an array-like object
        const keys = Object.keys(responseData);
        const numericKeys = keys.filter((k) => /^\d+$/.test(k));
        if (numericKeys.length > 0) {
          const lastKey = numericKeys.sort((a, b) => Number(a) - Number(b)).pop()!;
          return responseData[lastKey];
        }
        return responseData;
      }
      return null;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Saves the Business Interruption worksheet data.
 * POST /business-policy/business-interruption
 */
export const saveBusinessInterruptionAsync = createAsyncThunk<
  IBusinessInterruptionFormValues,
  { data: IBusinessInterruptionFormValues; customerPolicyId: string | number },
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/saveBusinessInterruption",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const {
        application_type,
        policy_number,
        date_prepared,
        effective_date,
        expiry_date,
        named_insured,
        description_of_operations,
        location_of_premises,
        actual_year_ended,
        estimated_year_ending,
        revenue_goods_services_actual,
        revenue_goods_services_estimated,
        revenue_rental_income_actual,
        revenue_rental_income_estimated,
        other_earnings_cash_discounts_actual,
        other_earnings_cash_discounts_estimated,
        other_earnings_commissions_actual,
        other_earnings_commissions_estimated,
        total_c_actual,
        total_c_estimated,
        expense_products_sold_actual,
        expense_products_sold_estimated,
        expense_materials_supplies_actual,
        expense_materials_supplies_estimated,
        expense_services_purchased_actual,
        expense_services_purchased_estimated,
        expense_utilities_actual,
        expense_utilities_estimated,
        expense_custom_1_label,
        expense_custom_1_actual,
        expense_custom_1_estimated,
        expense_custom_2_label,
        expense_custom_2_actual,
        expense_custom_2_estimated,
        expense_custom_3_label,
        expense_custom_3_actual,
        expense_custom_3_estimated,
        total_d_actual,
        total_d_estimated,
        ordinary_payroll_expense_actual,
        ordinary_payroll_expense_estimated,
        business_income_f_actual,
        business_income_f_estimated,
        period_of_indemnity,
        period_of_indemnity_other_months,
        amount_of_insurance,
        payroll_coverage_period,
        payroll_coverage_period_other_days,
        applicant_signature,
        applicant_date_signed,
        applicant_print_name,
        applicant_title,
        broker_signature,
        broker_date_signed,
      } = payload.data;

      const res = await api.post("/policy/create-policy-business-interruption", {
        application_type,
        policy_number,
        date_prepared: normalizeDateField(date_prepared),
        effective_date: normalizeDateField(effective_date),
        expiry_date: normalizeDateField(expiry_date),
        named_insured,
        description_of_operations,
        location_of_premises,
        actual_year_ended,
        estimated_year_ending,
        revenue_goods_services_actual,
        revenue_goods_services_estimated,
        revenue_rental_income_actual,
        revenue_rental_income_estimated,
        other_earnings_cash_discounts_actual,
        other_earnings_cash_discounts_estimated,
        other_earnings_commissions_actual,
        other_earnings_commissions_estimated,
        total_c_actual,
        total_c_estimated,
        expense_products_sold_actual,
        expense_products_sold_estimated,
        expense_materials_supplies_actual,
        expense_materials_supplies_estimated,
        expense_services_purchased_actual,
        expense_services_purchased_estimated,
        expense_utilities_actual,
        expense_utilities_estimated,
        expense_custom_1_label,
        expense_custom_1_actual,
        expense_custom_1_estimated,
        expense_custom_2_label,
        expense_custom_2_actual,
        expense_custom_2_estimated,
        expense_custom_3_label,
        expense_custom_3_actual,
        expense_custom_3_estimated,
        total_d_actual,
        total_d_estimated,
        ordinary_payroll_expense_actual,
        ordinary_payroll_expense_estimated,
        business_income_f_actual,
        business_income_f_estimated,
        period_of_indemnity,
        period_of_indemnity_other_months,
        amount_of_insurance,
        payroll_coverage_period,
        payroll_coverage_period_other_days,
        applicant_signature,
        applicant_date_signed: normalizeDateField(applicant_date_signed),
        applicant_print_name,
        applicant_title,
        broker_signature,
        broker_date_signed: normalizeDateField(broker_date_signed),
        customer_policy_id: payload.customerPolicyId,
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Saved successfully",
          displayAs: "snackbar",
        })
      );

      return res.data.data;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Fetches the list of Business Interruption worksheets for a given customer policy.
 * GET /business-policy/business-interruption/list
 */
export const fetchBusinessInterruptionListAsync = createAsyncThunk<
  {
    data: IBusinessInterruptionRecord[];
    totalRecords: number;
  },
  IBusinessInterruptionListFetchParams,
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/fetchBusinessInterruptionList",
  async (params, { rejectWithValue, dispatch }) => {
    try {
      let url = `/policy/get-policy-business-interruption?customer_policy_id=${params.customer_policy_id}&pageNo=${params.pageNo}&pageLimit=${params.pageLimit}&itemPerPage=${params.itemPerPage}`;

      if (params.from_date) {
        url += `&from_date=${params.from_date}`;
      }
      if (params.to_date) {
        url += `&to_date=${params.to_date}`;
      }

      const res = await api.get(url);
      const data: IBusinessInterruptionRecord[] = res.data.data;
      const totalRecords: number = res.data.totalRecords;

      return {
        data: data || [],
        totalRecords: totalRecords || 0,
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);


/**
 * Feature: lead-binder-additional-tabs
 * Lead-scoped Business Interruption list fetch. Keyed by `lead_id` (never
 * `customer_policy_id`), routed through the centralized endpoint map. On failure/timeout
 * it rejects, flipping the list to the Failed load state (retry surfaced by the sub-tab)
 * and shows an error message (Requirements 4.1, 4.5, 4.9). Mirrors
 * `fetchLeadBusinessRiskListAsync`.
 * GET /policy/get-policy-business-interruption?lead_id={leadId}
 */
export const fetchLeadBusinessInterruptionListAsync = createAsyncThunk<
  {
    data: IBusinessInterruptionRecord[];
    totalRecords: number;
  },
  ILeadScheduleScope,
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/fetchLeadBusinessInterruptionList",
  async (scope, { rejectWithValue, dispatch }) => {
    try {
      const request = buildBinderRequest(
        "business-interruption",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const res = await api.get(request.url, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessInterruptionRecord[] = res.data.data;
      const totalRecords: number = res.data.totalRecords;

      return {
        data: data || [],
        totalRecords: totalRecords || 0,
      };
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Business Interruption binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Feature: lead-binder-additional-tabs
 * Lead-scoped Business Interruption single-record fetch. Keyed by `lead_id` (never
 * `customer_policy_id`), routed through the centralized endpoint map. Mirrors
 * `fetchBusinessInterruptionAsync` but for the lead-binder in-place edit form
 * (Requirements 4.1, 4.7). When `incrementalNumber` is present it is appended so a
 * specific worksheet is edited; otherwise the latest record is returned.
 * GET /policy/get-policy-business-interruption?lead_id={leadId}
 */
export const fetchLeadBusinessInterruptionAsync = createAsyncThunk<
  IBusinessInterruptionFormValues | null,
  ILeadScheduleScope & { incrementalNumber?: string | number | null },
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/fetchLeadBusinessInterruption",
  async (scope, { rejectWithValue, dispatch }) => {
    try {
      const request = buildBinderRequest(
        "business-interruption",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let url = request.url;
      if (scope.incrementalNumber) {
        url += `&incremental_number=${scope.incrementalNumber}`;
      }
      const res = await api.get(url, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const responseData = res.data.data;
      // API returns an array of records — use the latest one (last item) for the form.
      if (Array.isArray(responseData) && responseData.length > 0) {
        return responseData[responseData.length - 1];
      }
      if (
        responseData &&
        typeof responseData === "object" &&
        !Array.isArray(responseData)
      ) {
        const keys = Object.keys(responseData);
        const numericKeys = keys.filter((k) => /^\d+$/.test(k));
        if (numericKeys.length > 0) {
          const lastKey = numericKeys
            .sort((a, b) => Number(a) - Number(b))
            .pop()!;
          return responseData[lastKey];
        }
        return responseData;
      }
      return null;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Business Interruption binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Feature: lead-binder-additional-tabs
 * Lead-scoped Business Interruption save. Keyed by `lead_id` (never
 * `customer_policy_id`), routed through the centralized endpoint map. Mirrors
 * `saveBusinessInterruptionAsync` but attaches `lead_id` to the request body so the
 * worksheet is persisted against the lead (Requirements 4.2, 4.3). Sends the identical
 * field set as the customer-policy save so no field is lost in lead scope.
 * POST /policy/create-policy-business-interruption  (body: { ...fields, lead_id })
 */
export const saveLeadBusinessInterruptionAsync = createAsyncThunk<
  IBusinessInterruptionFormValues,
  { data: IBusinessInterruptionFormValues; leadId: string },
  { rejectValue: APIError }
>(
  "businessPolicyBusinessInterruption/saveLeadBusinessInterruption",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const request = buildBinderRequest(
        "business-interruption",
        { kind: "lead", leadId: payload.leadId },
        "save"
      );
      const {
        application_type,
        policy_number,
        date_prepared,
        effective_date,
        expiry_date,
        named_insured,
        description_of_operations,
        location_of_premises,
        actual_year_ended,
        estimated_year_ending,
        revenue_goods_services_actual,
        revenue_goods_services_estimated,
        revenue_rental_income_actual,
        revenue_rental_income_estimated,
        other_earnings_cash_discounts_actual,
        other_earnings_cash_discounts_estimated,
        other_earnings_commissions_actual,
        other_earnings_commissions_estimated,
        total_c_actual,
        total_c_estimated,
        expense_products_sold_actual,
        expense_products_sold_estimated,
        expense_materials_supplies_actual,
        expense_materials_supplies_estimated,
        expense_services_purchased_actual,
        expense_services_purchased_estimated,
        expense_utilities_actual,
        expense_utilities_estimated,
        expense_custom_1_label,
        expense_custom_1_actual,
        expense_custom_1_estimated,
        expense_custom_2_label,
        expense_custom_2_actual,
        expense_custom_2_estimated,
        expense_custom_3_label,
        expense_custom_3_actual,
        expense_custom_3_estimated,
        total_d_actual,
        total_d_estimated,
        ordinary_payroll_expense_actual,
        ordinary_payroll_expense_estimated,
        business_income_f_actual,
        business_income_f_estimated,
        period_of_indemnity,
        period_of_indemnity_other_months,
        amount_of_insurance,
        payroll_coverage_period,
        payroll_coverage_period_other_days,
        applicant_signature,
        applicant_date_signed,
        applicant_print_name,
        applicant_title,
        broker_signature,
        broker_date_signed,
      } = payload.data;

      const res = await api.post(request.url, {
        application_type,
        policy_number,
        date_prepared: normalizeDateField(date_prepared),
        effective_date: normalizeDateField(effective_date),
        expiry_date: normalizeDateField(expiry_date),
        named_insured,
        description_of_operations,
        location_of_premises,
        actual_year_ended,
        estimated_year_ending,
        revenue_goods_services_actual,
        revenue_goods_services_estimated,
        revenue_rental_income_actual,
        revenue_rental_income_estimated,
        other_earnings_cash_discounts_actual,
        other_earnings_cash_discounts_estimated,
        other_earnings_commissions_actual,
        other_earnings_commissions_estimated,
        total_c_actual,
        total_c_estimated,
        expense_products_sold_actual,
        expense_products_sold_estimated,
        expense_materials_supplies_actual,
        expense_materials_supplies_estimated,
        expense_services_purchased_actual,
        expense_services_purchased_estimated,
        expense_utilities_actual,
        expense_utilities_estimated,
        expense_custom_1_label,
        expense_custom_1_actual,
        expense_custom_1_estimated,
        expense_custom_2_label,
        expense_custom_2_actual,
        expense_custom_2_estimated,
        expense_custom_3_label,
        expense_custom_3_actual,
        expense_custom_3_estimated,
        total_d_actual,
        total_d_estimated,
        ordinary_payroll_expense_actual,
        ordinary_payroll_expense_estimated,
        business_income_f_actual,
        business_income_f_estimated,
        period_of_indemnity,
        period_of_indemnity_other_months,
        amount_of_insurance,
        payroll_coverage_period,
        payroll_coverage_period_other_days,
        applicant_signature,
        applicant_date_signed: normalizeDateField(applicant_date_signed),
        applicant_print_name,
        applicant_title,
        broker_signature,
        broker_date_signed: normalizeDateField(broker_date_signed),
        ...request.body,
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Saved successfully",
          displayAs: "snackbar",
        })
      );

      return res.data.data;
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
      return rejectWithValue({
        message: err.response?.data?.message || "Something went wrong",
        status: err.response?.status,
      });
    }
  }
);

/**
 * Downloads the Business Interruption worksheet as a PDF.
 * GET /policy/get-policy-business-interruption-worksheet-pdf?customer_policy_id=X&incremental_number=Y
 */
export const downloadBusinessInterruptionPDF =
  (
    customerPolicyId: number,
    incrementalNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-business-interruption-worksheet-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}`,
        {
          responseType: "arraybuffer",
        }
      );

      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "business-interruption-worksheet.pdf";
      link.click();

      URL.revokeObjectURL(pdfUrl);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/**
 * Previews the Business Interruption worksheet as HTML in a new tab.
 * GET /policy/get-policy-business-interruption-worksheet-pdf?customer_policy_id=X&incremental_number=Y&isBusinessInterruptionPreview=true
 */
export const previewBusinessInterruptionPDF =
  (
    customerPolicyId: number,
    incrementalNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/policy/get-policy-business-interruption-worksheet-pdf?customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&isBusinessInterruptionPreview=true`
      );

      const htmlContent = res.data.data;
      if (typeof htmlContent === "string") {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(htmlContent);
          newTab.document.close();
        }
      } else if (typeof htmlContent === "object") {
        for (const key in htmlContent) {
          const newTab = window.open("", "_blank");
          if (newTab) {
            newTab.document.write(htmlContent[key]);
            newTab.document.close();
          }
        }
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Something went wrong",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
